import mainStyle from "../clients.module.scss";
import { ReactComponent as ToggleOnIcon } from "assets/images/icons/Toggle-On.svg";
import { ReactComponent as ToggleOffIcon } from "assets/images/icons/Toggle-Off.svg";

import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import useDataSourceManager from "hooks/useDataSourceManager";

import CollapseCard from "components/Collapse";
import GlobalAlert from "components/Notification";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import InputField from "components/InputField";

import Api from "../util/api";
import { getIsFormInvalid } from '../util/func';

import { uuids } from '../util/const';

const SettingsContent = ({
  auth,
  selectedClient,
  selectedSiteId,
  onSettingsApplyCallback,
  showEditGeneral,
  setIsLoading,
}) => {
  const theme = useTheme();
  const { sendRequest, getData, dataMap } = useDataSourceManager();
  const title = 'Device';
  const readOnlyStyle = {
    color: theme.palette.primary.greyPrimary,
    fontSize: theme.typography.customFontSize[2],
    fontWeight: theme.typography.customFontWeight[1],
  };
  const api = Api.init(auth, selectedSiteId);

  const [clonedSelectedClient, setClonedSelectedClient] = useState(selectedClient);
  const isFormInvalid = useMemo(() =>  getIsFormInvalid(clonedSelectedClient), [clonedSelectedClient]);

  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: '',
    message: '',
  });

  const isModified = useMemo(() => {
    return clonedSelectedClient.isBlocked !== selectedClient.isBlocked ||
      clonedSelectedClient.clientName !== selectedClient.clientName;
  }, [clonedSelectedClient, selectedClient]);

  const handleHandleCLientName = value => {
    setClonedSelectedClient((pre) => ({
      ...pre,
      clientName: value
    }));
  }

  const handleBlockToggle = () => {
    setClonedSelectedClient((prev) => ({
      ...prev,
      isBlocked: !prev.isBlocked,
    }));
  }

  const handleCancel = () => {
    setClonedSelectedClient(selectedClient);
  }

  const handleApply = () => {
    if(!clonedSelectedClient) return;

    setIsLoading(true);
    const { apiKey, params } = api.getUpdateRequestParams(clonedSelectedClient.clientId);
    const payload = {
      name: clonedSelectedClient.clientName,
    };

    sendRequest(
      apiKey,
      uuids.updateClient,
      params,
      payload,
    );
  }

  useEffect(() => {
    setIsLoading(true);
    setClonedSelectedClient(selectedClient);
    setIsLoading(false);
  }, [selectedClient]);

  useEffect(() => {
    const responseData = getData(uuids.updateClient);

    if (responseData) {
      setIsLoading(false);
      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: 'error',
          message: responseData.error?.message || 'Failed to update client settings.',
        });
      } else {
        onSettingsApplyCallback && onSettingsApplyCallback();
      }

      setIsLoading(false);
    }
  }, [dataMap]);

  if (!selectedClient) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          p: 4,
          boxSizing: "border-box",
          gap: "20px",
          position: "relative",
        }}
      >
        <CollapseCard title={title} >
          <Box className={mainStyle["section-container"]}>
            {/* Connected to */}
            <Box>
              <Box className={mainStyle["row-style"]}>
                <Typography className={mainStyle["collapse-typography"]}>
                  Name
                </Typography>
                <InputField
                  value={clonedSelectedClient?.clientName || ""}
                  onChange={value => showEditGeneral && handleHandleCLientName(value)}
                />
              </Box>
            </Box>
            {/* Block / Allow */}
            {/* <Box>
              <Box className={mainStyle["row-style"]}>
                <Typography className={mainStyle["collapse-typography"]}>
                  Block
                </Typography>
                <Box
                  onClick={() => showEditGeneral && handleBlockToggle()}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    width: 40,
                    height: 22,
                  }}
                >
                  {clonedSelectedClient.isBlocked ? <ToggleOnIcon /> : <ToggleOffIcon />}
                </Box>
              </Box>
            </Box> */}
          </Box>
        </CollapseCard>
        <Box
          sx={{
            width: '107%',
            display: isModified ? "flex" : "none",
            justifyContent: "center",
            gap: 2,
            padding: "16px",
            backgroundColor: "primary.lightBlue",
            transform: "translateX(-16px)",
          }}
        >
          <OutlineButton width="100px" height="32px" onClick={handleCancel}>
            Cancel
          </OutlineButton>
          <SolidButton width="100px" height="32px" disabled={isFormInvalid} onClick={handleApply}>
            Apply
          </SolidButton>
        </Box>
      </Box>

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default SettingsContent;
